// ✅ src/pages/VehicleV2/VehicleHSDFillingWholeReport.jsx

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaPrint, FaFilter, FaArrowLeft, FaInfoCircle } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehicleHsdFillingRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMYYYY = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const parseDDMMYYYY = (s) => {
  const v = toStr(s);
  if (!v) return null;
  const [ddS, mmS, yyyyS] = v.split("/");
  const dd = Number(ddS);
  const mm = Number(mmS);
  const yyyy = Number(yyyyS);
  if (!dd || !mm || !yyyy) return null;

  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const today0 = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

const exportToCsv = (rows, filename = "vehicle-hsd-filling-report.csv") => {
  if (!rows?.length) return toast.info("No rows to export");
  const headers = Object.keys(rows[0] || {});
  const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function VehicleHSDFillingWholeReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // session/local storage (match your other pages)
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType = (localStorage.getItem("branchtype") || "HO").toUpperCase();
  const sessionBranchId = toInt(localStorage.getItem("branchid"), 0);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // dropdowns
  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([{ label: "Select", value: "Select" }]);

  const months = useMemo(
    () => [
      { label: "Select", value: "Select" },
      { label: "January", value: "January" },
      { label: "February", value: "February" },
      { label: "March", value: "March" },
      { label: "April", value: "April" },
      { label: "May", value: "May" },
      { label: "June", value: "June" },
      { label: "July", value: "July" },
      { label: "August", value: "August" },
      { label: "September", value: "September" },
      { label: "October", value: "October" },
      { label: "November", value: "November" },
      { label: "December", value: "December" },
    ],
    []
  );

  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);

  // auto-complete
  const [vehCodeQuery, setVehCodeQuery] = useState("");
  const [vehCodeOpts, setVehCodeOpts] = useState([]);
  const vehCodeTimer = useRef(null);

  const [vehRegQuery, setVehRegQuery] = useState("");
  const [vehRegOpts, setVehRegOpts] = useState([]);
  const vehRegTimer = useRef(null);

  const [vehNameQuery, setVehNameQuery] = useState("");
  const [vehNameOpts, setVehNameOpts] = useState([]);
  const vehNameTimer = useRef(null);

  // results
  const [rows, setRows] = useState([]);

  const [filters, setFilters] = useState({
    ownHire: "OWN", // rbOwn / rbHire
    branchId: "Select",

    vehicleCode: "",
    vehicleRegno: "",
    vehicleName: "",

    month: "Select",
    year: "Select",

    fromDate: "",
    toDate: "",

    vehicleMainTypeId: "Select",
  });

  const isSelect = (v) => {
    const s = toStr(v);
    return !s || s.toLowerCase() === "select";
  };

  /* ---------------- validation ---------------- */
  const validate = (f = filters) => {
    if (!toStr(f.vehicleCode)) {
      toast.error("Vehicle Code is required");
      return false;
    }

    const fd = parseDDMMYYYY(f.fromDate);
    const td = parseDDMMYYYY(f.toDate);

    if (f.fromDate && !fd) return toast.error("Invalid From Date (dd/MM/yyyy)") && false;
    if (f.toDate && !td) return toast.error("Invalid To Date (dd/MM/yyyy)") && false;

    if (fd && fd.getTime() > today0().getTime()) return toast.error("From Date must be <= today") && false;
    if (td && td.getTime() > today0().getTime()) return toast.error("To Date must be <= today") && false;
    if (fd && td && td.getTime() < fd.getTime()) return toast.error("From Date must be <= To Date") && false;

    return true;
  };

  /* ---------------- build params once (used for list + print) ---------------- */
  const buildParams = (f = filters) => ({
    ownHire: f.ownHire,
    branchId: isSelect(f.branchId) ? "Select" : f.branchId,

    vehicleCode: toStr(f.vehicleCode),
    vehicleRegno: toStr(f.vehicleRegno),
    vehicleName: toStr(f.vehicleName),

    month: f.month || "Select",
    year: f.year || "Select",

    fromDate: toStr(f.fromDate),
    toDate: toStr(f.toDate),

    vehicleMainTypeId: f.vehicleMainTypeId || "Select",

    // session context (if backend needs)
    branchType,
    sessionBranchId,
    userId,
  });

  /* ---------------- API ---------------- */
  const fetchInit = async (ownHire = filters.ownHire) => {
    const params = { branchType, sessionBranchId, userId, ownHire };
    const { data } = await axios.get(`${API_BASE}/init`, { params });
    if (data?.success === false) throw new Error(data?.message || "Init failed");

    setBranches(data?.branches?.length ? data.branches : [{ label: "Select", value: "Select" }]);

    if (data?.years?.length) {
      setYears(data.years);
    } else {
      const y = new Date().getFullYear();
      const fallback = [{ label: "Select", value: "Select" }];
      for (let i = 0; i < 15; i++) fallback.push({ label: String(y - i), value: String(y - i) });
      setYears(fallback);
    }

    setVehicleMainTypes(data?.vehicleSubTypes?.length ? data.vehicleSubTypes : [{ label: "Select", value: "Select" }]);
  };

  const fetchList = async (f = filters) => {
    if (!validate(f)) return;

    const params = buildParams(f);
    const { data } = await axios.get(`${API_BASE}/list`, { params });
    if (data?.success === false) throw new Error(data?.message || "Search failed");

    setRows(data?.rows || []);
    toast.success(`Found ${data?.rows?.length || 0} record(s)`);
  };

  /* ---------------- UPDATED: Print = navigate to print page with state ---------------- */
  const openPrint = (f = filters) => {
    if (!validate(f)) return;

    const params = buildParams(f);

    // ✅ SAME TAB redirect (as you asked)
    navigate("/vehicle/VehicleHsdFillingWholeRpt_Print", {
      state: { filters: params },
    });
  };

  const fetchAC = async (kind, q) => {
    const query = toStr(q);
    if (!query) return [];

    const params = {
      q: query,
      branchId: isSelect(filters.branchId) ? "" : filters.branchId,
      ownHire: filters.ownHire,
    };

    let url = "";
    if (kind === "CODE") url = `${API_BASE}/ac/vehicleCode`;
    if (kind === "REG") url = `${API_BASE}/ac/vehicleRegno`;
    if (kind === "NAME") url = `${API_BASE}/ac/vehicleName`;

    const { data } = await axios.get(url, { params });
    if (data?.success === false) throw new Error(data?.message || "Autocomplete failed");

    const items = data?.items || [];
    return items.map((x) => (typeof x === "string" ? { label: x, value: x } : x));
  };

  /* ---------------- events ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleOwnHire = (ownHire) => {
    setFilters((p) => ({
      ...p,
      ownHire,
      vehicleCode: "",
      vehicleRegno: "",
      vehicleName: "",
    }));
    setVehCodeQuery("");
    setVehRegQuery("");
    setVehNameQuery("");
    setVehCodeOpts([]);
    setVehRegOpts([]);
    setVehNameOpts([]);
    setRows([]);

    fetchInit(ownHire).catch((e) => toast.error(e?.message || "Failed to reload"));
  };

  const handleVehCodeInput = (e) => {
    const v = e.target.value;
    setVehCodeQuery(v);
    setFilters((p) => ({ ...p, vehicleCode: v }));

    if (vehCodeTimer.current) clearTimeout(vehCodeTimer.current);
    vehCodeTimer.current = setTimeout(() => {
      fetchAC("CODE", v)
        .then(setVehCodeOpts)
        .catch((err) => toast.error(err?.message || "Vehicle code lookup failed"));
    }, 250);
  };

  const handleVehRegInput = (e) => {
    const v = e.target.value;
    setVehRegQuery(v);
    setFilters((p) => ({ ...p, vehicleRegno: v }));

    if (vehRegTimer.current) clearTimeout(vehRegTimer.current);
    vehRegTimer.current = setTimeout(() => {
      fetchAC("REG", v)
        .then(setVehRegOpts)
        .catch((err) => toast.error(err?.message || "Vehicle regno lookup failed"));
    }, 250);
  };

  const handleVehNameInput = (e) => {
    const v = e.target.value;
    setVehNameQuery(v);
    setFilters((p) => ({ ...p, vehicleName: v }));

    if (vehNameTimer.current) clearTimeout(vehNameTimer.current);
    vehNameTimer.current = setTimeout(() => {
      fetchAC("NAME", v)
        .then(setVehNameOpts)
        .catch((err) => toast.error(err?.message || "Vehicle name lookup failed"));
    }, 250);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await fetchList(filters);
    } catch (e) {
      toast.error(e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- init ---------------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchInit(filters.ownHire);

        setFilters((p) => ({
          ...p,
          fromDate: p.fromDate || fmtDDMMYYYY(new Date()),
          toDate: p.toDate || fmtDDMMYYYY(new Date()),
        }));
      } catch (e) {
        toast.error(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- table render ---------------- */
  const renderTable = (list) => {
    if (!list?.length) return null;

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${buttonClass} text-white`}>
            <tr>
              <th className="px-3 py-2 text-center whitespace-nowrap">S.No</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Vehicle Main Type</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Vehicle Name (Link)</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Vehicle Code</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Vehicle Name</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Vehicle Regno</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Total Working Hr</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Total HSD Filling (Hr)</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Total Working KM</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Total HSD Filling (KM)</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">HSD Consumption Hr</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">HSD Consumption KM</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Last Updated Cummulative Hr</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Last Updated Cummulative KM</th>
              <th className="px-3 py-2 text-center whitespace-nowrap">Last Updated Date</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {list.map((r, idx) => {
              // ✅ pick a vehicle id safely (change priority if you know exact field)
              const vehicleId =
                r?.fld_vehicle_id ??
                r?.fld_vehicleid ??
                r?.vehicleId ??
                r?.VehicleId ??
                r?.fld_vehicle_code; // fallback if backend doesn't give id

              const vehicleCode = toStr(r?.fld_vehicle_code);

              return (
                <tr
                  key={`${vehicleCode}-${idx}`}
                  className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors`}
                >
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {idx + 1}
                  </td>

                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.VehicleMainType)}
                  </td>

                  {/* ✅ UPDATED: click -> navigate to /vehicle/VehicleHsdFillingReport with id */}
                  <td className="px-3 py-2 text-center text-sm">
                    <button
                      type="button"
                      className="text-blue-600 hover:underline font-semibold"
                      onClick={() => {
                        navigate("/vehicle/VehicleHsdFillingReport", {
                          state: {
                            vehicleId,
                            vehicleCode,
                            // optional: carry current filters too
                            fromWholeReportFilters: buildParams(filters),
                          },
                        });
                      }}
                      title="Open Vehicle HSD Filling Report"
                    >
                      {vehicleCode}
                    </button>
                  </td>

                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {vehicleCode}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_vehicle_name)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_vehicle_regno)}
                  </td>

                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_hsdfillingWorkinhr)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_hsdfillinghr)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_hsdfillingWorkingKM)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_hsdfillingkm)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_diselconsumptionhr)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_diselconsumptionkm)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.cumulativehrs)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.cumulativekm)}
                  </td>
                  <td className={`px-3 py-2 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                    {toStr(r?.fld_progressentrydate)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const hasRows = rows.length > 0;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE HSD FILLING HR/KM REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle HSD Filling HR/KM Report
              </h1>
              {loading && <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</span>}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${showFilters ? " bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${showFilters ? " bg-gray-50" : ""}`
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`rounded-xl p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                  <FaFilter /> FILTRATION AREA
                </h3>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  <b>INFRA SCREEN :</b> B-54
                </div>
              </div>

              {/* Own/Hire */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className={`rounded-lg p-4 border ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"}`}>
                  <div className={labelClass}>Vehicle Type</div>
                  <div className="flex gap-6 items-center">
                    <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      <input type="radio" checked={filters.ownHire === "OWN"} onChange={() => handleOwnHire("OWN")} /> Own Vehicle
                    </label>
                    <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      <input type="radio" checked={filters.ownHire === "HIRE"} onChange={() => handleOwnHire("HIRE")} /> Hire Vehicle
                    </label>
                  </div>
                </div>

                <div className={`rounded-lg p-4 border ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"}`}>
                  <div className={labelClass}>Branch</div>
                  <select name="branchId" value={filters.branchId} onChange={handleChange} className={inputClass}>
                    {(branches || []).map((b) => (
                      <option key={toStr(b.value)} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Vehicle Code / Regno / Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>
                    Vehicle Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={vehCodeQuery}
                    onChange={handleVehCodeInput}
                    className={inputClass}
                    placeholder="Type vehicle code..."
                    list="vehCodeList"
                  />
                  <datalist id="vehCodeList">
                    {(vehCodeOpts || []).map((o) => (
                      <option key={o.value || o.label} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Vehicle RegistrationNumber</label>
                  <input
                    type="text"
                    value={vehRegQuery}
                    onChange={handleVehRegInput}
                    className={inputClass}
                    placeholder="Type regno..."
                    list="vehRegList"
                  />
                  <datalist id="vehRegList">
                    {(vehRegOpts || []).map((o) => (
                      <option key={o.value || o.label} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={vehNameQuery}
                    onChange={handleVehNameInput}
                    className={inputClass}
                    placeholder="Type vehicle name..."
                    list="vehNameList"
                  />
                  <datalist id="vehNameList">
                    {(vehNameOpts || []).map((o) => (
                      <option key={o.value || o.label} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Month/Year + From/To */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    {(years || []).map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date (dd/MM/yyyy)</label>
                  <input
                    type="text"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date (dd/MM/yyyy)</label>
                  <input
                    type="text"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                  />
                </div>
              </div>

              {/* Vehicle Main Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainTypeId" value={filters.vehicleMainTypeId} onChange={handleChange} className={inputClass}>
                    {(vehicleMainTypes || []).map((t) => (
                      <option key={toStr(t.value)} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end justify-end gap-3">
                  <button
                    onClick={() => exportToCsv(rows, "vehicle-hsd-filling-report.csv")}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-semibold shadow-lg"
                    disabled={!rows.length || loading}
                  >
                    Excel
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg`}
                    disabled={loading}
                  >
                    <FaSearch /> Search
                  </button>

                  {/* ✅ UPDATED Print */}
                  <button
                    onClick={() => openPrint(filters)}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg`}
                    disabled={loading}
                    title="Print"
                  >
                    <FaPrint /> Print
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {!rows.length ? (
            <div className="text-center py-16">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>No Records Found</h3>
              <p className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>Select filters and click Search.</p>
            </div>
          ) : (
            <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className={`px-4 py-3 font-bold ${isDark ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-900"}`}>
                Result List
              </div>
              {renderTable(rows)}
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
